import { gql } from '@apollo/client';

// Staff only: a parent or link-token caller that selects reportCardAccess is refused, and the refusal
// nulls the whole row. Never spread this into a query a parent or token page runs.
export const REPORT_CARD_ACCESS_FIELDS = gql`
  fragment ReportCardAccessFields on PlayerAssessment {
    _id
    reportCardAccess {
      status
      hasData
      releasedAt
      revokedAt
    }
  }
`;

// What ReportCardActions reads: the link's code, each contact's tokens, the age group and the status. Staff only.
export const REPORT_CARD_ACTIONS_FIELDS = gql`
  fragment ReportCardActionsFields on PlayerAssessment {
    _id
    ageGroup {
      _id
      assessmentId
    }
    player {
      _id
      code
      firstName
      lastName
      nickname
    }
    contacts {
      _id
      name
      email
      phone
      reportCardAccessToken
      reportCardAccessExpiry
      contactAccessTokens {
        token
        playerAssessmentId
        ageGroupId
        expiry
        createdAt
      }
    }
    ...ReportCardAccessFields
  }

  ${REPORT_CARD_ACCESS_FIELDS}
`;

// The player page's report card section. Listed in REPORT_CARD_ROW_QUERIES: a send changes its contacts' tokens.
export const GET_PLAYER_REPORT_CARD = gql`
  query GetPlayerReportCard($_id: ID!) {
    playerAssessment(_id: $_id) {
      ...ReportCardActionsFields
    }
  }

  ${REPORT_CARD_ACTIONS_FIELDS}
`;

export const SET_PLAYER_REPORT_CARD_ACCESS = gql`
  mutation SetPlayerReportCardAccess($assessmentId: ID!, $playerAssessmentIds: [ID!]!, $enabled: Boolean!) {
    setPlayerReportCardAccess(assessmentId: $assessmentId, playerAssessmentIds: $playerAssessmentIds, enabled: $enabled) {
      playerAssessments {
        ...ReportCardAccessFields
      }
      notReleasedPlayerAssessmentIds
    }
  }

  ${REPORT_CARD_ACCESS_FIELDS}
`;

export const REPORT_CARD_AVAILABILITY_FIELDS = gql`
  fragment ReportCardAvailabilityFields on ReportCardAvailability {
    ageGroupId
    releasedAt
    revokedAt
    includeNonConcluded
    includeNonConcludedSource
    ageGroupIncludeNonConcluded
    counts {
      noData
      notReleased
      available
      revoked
      ageGroupRevoked
      notReleasedConcluded
      notReleasedNotConcluded
    }
  }
`;

// One availability per age group and no _id: keyed by age group so the access mutation's result and the
// include-non-concluded save land on the cached panel. Registered in createApolloClient.
export const REPORT_CARD_AVAILABILITY_TYPE_POLICY = { ReportCardAvailability: { keyFields: ['ageGroupId'] } };

// Cached under the age group (REPORT_CARD_AVAILABILITY_TYPE_POLICY), so the access mutation's result and the
// include-non-concluded save update every view of it. Per-player access writes refetch it (counts).
export const GET_REPORT_CARD_AVAILABILITY = gql`
  query GetReportCardAvailability($assessmentId: ID!, $ageGroupId: ID!) {
    reportCardAvailability(assessmentId: $assessmentId, ageGroupId: $ageGroupId) {
      ...ReportCardAvailabilityFields
    }
  }

  ${REPORT_CARD_AVAILABILITY_FIELDS}
`;

export const SET_AGE_GROUP_REPORT_CARD_ACCESS = gql`
  mutation SetAgeGroupReportCardAccess($assessmentId: ID!, $ageGroupId: ID!, $enabled: Boolean!) {
    setAgeGroupReportCardAccess(assessmentId: $assessmentId, ageGroupId: $ageGroupId, enabled: $enabled) {
      ...ReportCardAvailabilityFields
    }
  }

  ${REPORT_CARD_AVAILABILITY_FIELDS}
`;

const REPORT_CARD_SEND_SKIPPED_FIELDS = gql`
  fragment ReportCardSendSkippedFields on ReportCardSendSkipped {
    notConcluded
    noData
    notReleased
    revoked
    alreadyReleased
    ageGroupRevoked
  }
`;

// The same player selection the send uses, so a zero here means the send would reach nobody.
export const REPORT_CARD_SEND_PREVIEW = gql`
  query ReportCardSendPreview($assessmentId: ID!, $ageGroupIds: [ID!]!, $playerAssessmentIds: [ID!], $resend: Boolean, $includeNonConcluded: Boolean) {
    reportCardSendPreview(assessmentId: $assessmentId, ageGroupIds: $ageGroupIds, playerAssessmentIds: $playerAssessmentIds, resend: $resend, includeNonConcluded: $includeNonConcluded) {
      playerCount
      notifiedContactCount
      emailCount
      smsCount
      accessContactCount
      unreachableContactCount
      unreachablePlayerCount
      skipped {
        ...ReportCardSendSkippedFields
      }
      revokedAgeGroupIds
    }
  }

  ${REPORT_CARD_SEND_SKIPPED_FIELDS}
`;

// Resolves to who the send told, so its outcome copy never relies on a preview read before it.
export const SEND_REPORT_CARDS = gql`
  mutation SendReportCards($assessmentId: ID!, $ageGroupIds: [ID!]!, $playerAssessmentIds: [ID!], $resend: Boolean, $includeNonConcluded: Boolean) {
    sendReportCards(assessmentId: $assessmentId, ageGroupIds: $ageGroupIds, playerAssessmentIds: $playerAssessmentIds, resend: $resend, includeNonConcluded: $includeNonConcluded) {
      playerCount
      alreadySentPlayerCount
      notifiedContactCount
      emailCount
      smsCount
      accessContactCount
      unreachableContactCount
      unreachablePlayerCount
      skipped {
        ...ReportCardSendSkippedFields
      }
    }
  }

  ${REPORT_CARD_SEND_SKIPPED_FIELDS}
`;
